import { useMemo } from 'react';
import { useOrchestratorList } from '../../hooks/useOrchestratorList';
import {
  getDuration,
  formatLocalDate,
  formatDuration,
} from '../../utils/dates';
import { BaseChartTable } from '../shared/BaseChartTable';
import { useNidNavigation } from '../../hooks/useNidNavigation';
import { renderCommonCells } from '../shared/chartTableHelpers';

interface CompletedChartTableProps {
  selectedNid?: string | null;
  onResetSelection: () => void;
}

export const CompletedChartTable = ({
  selectedNid,
  onResetSelection,
}: CompletedChartTableProps) => {
  const { orchestrators } = useOrchestratorList();
  const { handleNidClick } = useNidNavigation();

  const tableData = useMemo(() => {
    const allData =
      orchestrators.data
        ?.map(o => {
          if (!o.end_at) return null;
          const { days, hours, minutes } = getDuration(o.start_at, o.end_at);
          const total = days * 24 + hours + minutes / 60;
          return {
            nid: o.nid,
            startDate: formatLocalDate(o.start_at),
            endDate: formatLocalDate(o.end_at),
            hours: Math.round(total * 100) / 100,
            totalDuration: formatDuration(o.start_at, o.end_at),
            rawData: o,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) ??
      [];

    return selectedNid
      ? allData.filter(item => item.nid === selectedNid)
      : allData;
  }, [orchestrators.data, selectedNid]);

  const exportData = useMemo(
    () =>
      tableData.map((item, index) => ({
        'No.': index + 1,
        NID: item.nid,
        'Fecha Inicio': item.startDate,
        'Fecha Fin': item.endDate,
        'Tiempo Total': item.totalDuration,
        'Horas Totales': item.hours,
      })),
    [tableData]
  );

  return (
    <BaseChartTable
      title="Datos de Completados"
      data={tableData}
      headers={[
        'No.',
        'NID',
        'Fecha Inicio',
        'Fecha Fin',
        'Tiempo Total',
        'Horas Totales',
      ]}
      renderRow={(row, index) => (
        <tr key={index}>
          {renderCommonCells(row, index, handleNidClick)}
          <td>{row.startDate}</td>
          <td>{row.endDate}</td>
          <td>{row.totalDuration}</td>
          <td>{row.hours}</td>
        </tr>
      )}
      exportData={exportData}
      exportFilename={`completados_${new Date().toISOString().split('T')[0]}`}
      hasSelection={!!selectedNid}
      onResetSelection={onResetSelection}
    />
  );
};
